import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fmt } from "./convoTraceFormat";

const CandidateTable = (props) => {
  const { title, rows } = props;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Table size="small" sx={{ "& td, & th": { py: 0.25, px: 1, border: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Bucket</TableCell>
            <TableCell>Meaning</TableCell>
            <TableCell align="right">Score</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              <TableCell>{row.udb_bucket_id}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{row.meaning || "—"}</TableCell>
              <TableCell align="right">{fmt(row.score)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// The collapsible raw ranker detail behind the insight: parsed query, the
// fusion table (semantic vs lexical per bucket), candidate lists, and the raw
// decision line. `expanded` (the page's "Advanced insights" toggle) sets the
// default open state; it stays togglable per-response and re-syncs on flip.
const ConvoTraceBreakdown = (props) => {
  const { trace, expanded = false } = props;
  const [open, setOpen] = useState(expanded);
  useEffect(() => setOpen(expanded), [expanded]);

  const stages = trace.stages || {};
  const exact = Array.isArray(stages.exact) ? stages.exact : [];
  const semantic = Array.isArray(stages.semantic) ? stages.semantic : [];
  const lexical = Array.isArray(stages.lexical) ? stages.lexical : [];
  const fusion = Array.isArray(stages.fusion) ? stages.fusion : [];

  return (
    <Accordion
      expanded={open}
      onChange={(_event, value) => setOpen(value)}
      disableGutters
      elevation={0}
      sx={{ bgcolor: "transparent", "&:before": { display: "none" }, "&.Mui-expanded": { m: 0 } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ px: 0, minHeight: 0, "& .MuiAccordionSummary-content": { my: 0.75 } }}
      >
        <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600 }}>
          Technical breakdown
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        {trace.sentence != null && (
          <Typography variant="caption" component="div" sx={{ color: "text.secondary", mb: 1 }}>
            Searched: “{trace.sentence}”
            {Array.isArray(trace.terms) && trace.terms.length > 0 ? ` · terms: ${trace.terms.join(", ")}` : ""}
          </Typography>
        )}

        {fusion.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Fusion — fused = max(semantic, lexical); source is the winning signal
            </Typography>
            <Table size="small" sx={{ "& td, & th": { py: 0.25, px: 1, border: 0 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Bucket</TableCell>
                  <TableCell>Meaning</TableCell>
                  <TableCell align="right">Semantic</TableCell>
                  <TableCell align="right">Lexical</TableCell>
                  <TableCell align="right">Fused</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fusion.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.udb_bucket_id}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{row.meaning || "—"}</TableCell>
                    <TableCell align="right">{fmt(row.semantic)}</TableCell>
                    <TableCell align="right">{fmt(row.lexical)}</TableCell>
                    <TableCell align="right">{fmt(row.fused)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={row.source === "lexical" ? "warning" : "primary"}
                        label={row.source}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {exact.length > 0 && <CandidateTable title="Exact matches" rows={exact} />}
        {fusion.length === 0 && semantic.length > 0 && <CandidateTable title="Semantic candidates" rows={semantic} />}
        {fusion.length === 0 && lexical.length > 0 && <CandidateTable title="Lexical candidates" rows={lexical} />}

        {trace.decision && (
          <Typography variant="caption" component="div" sx={{ color: "text.disabled", mt: 0.5 }}>
            {trace.decision}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ConvoTraceBreakdown;
